import { useUser } from "components/clerk";
import { Box, Button, CloseIcon, H2, HStack, Icon, Image, Pressable, Typography } from "components/ui";
import { Link, usePathname, useRouter } from "expo-router";
import React, { useMemo, useState } from "react";
import { Platform } from "react-native";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import { useIsAdminSection, useSiteRegion } from "utils/hooks/useSiteSection";
import { useUserPermission } from "utils/hooks/useUserPermission";
import { navigationCollection, navigationCollectionPublic, subNavigationCollection, subNavigationCollectionPublic } from "../../navigation/navigationData";
import Container from "../Container";
import MegaMenuContent from "./MegaMenuContent";

type Props = {};
const HeaderPrimary = (props: Props) => {
	let currentPath = usePathname();
	const { isOpen: siteSearchOpen, close: closeSearch } = useSiteSearch();
	const isAdminSection = useIsAdminSection();
	const router = useRouter();
	const region = useSiteRegion();
	const { canViewReports } = useUserPermission();
	const [hoveredItem, setHoveredItem] = useState<string | null>(null);
	const [megamenuOpen, setMegamenuOpen] = useState(false);
	const [hoveredNavItem, setHoveredNavItem] = useState<string | null>(null);

	let { isSignedIn, user } = useUser();
	let topNavigation = navigationCollectionPublic;
	let subNavigation = subNavigationCollectionPublic;
	if (isSignedIn) {
		topNavigation = navigationCollection;
		subNavigation = subNavigationCollection;
	}

	var isSuperAdmin = false;
	if (user && user.publicMetadata && user.publicMetadata.isSuperAdmin) {
		isSuperAdmin = true;
	}

	const handleNavHover = (href: string) => {
		setHoveredItem(href);
		setHoveredNavItem(href);
		// Don't show megamenu for Home page
		if (href !== "/") {
			setMegamenuOpen(true);
		} else {
			setMegamenuOpen(false);
		}
	};

	const handleNavLeave = () => {
		// Don't immediately close - let the megamenu handle this
	};

	const handleMegamenuHover = () => {
		setMegamenuOpen(true);
	};

	const handleMegamenuLeave = () => {
		setMegamenuOpen(false);
		setHoveredItem(null);
		setHoveredNavItem(null);
	};

	const logo = useMemo(() => {
		return region === "au" ? require("../../../assets/logo.svg") : require("../../../assets/logo-nz-new.svg");
	}, [region]);

	if (Platform.OS !== "web") {
		return;
	}
	return (
		<Container sx={{ zIndex: 9 }} noPadding>
			<Box backgroundColor="$neutral25" paddingHorizontal={28} paddingVertical={region === "au" ? 24 : 14} borderTopLeftRadius={8} borderTopRightRadius={8}>
				{siteSearchOpen && (
					<Box
						flexDirection="row"
						justifyContent="space-between"
						sx={{
							position: "absolute",
							left: 0,
							top: 0,
							right: 0,
							bottom: 0,
							zIndex: 90,
							backgroundColor: "white",
							padding: 24,
						}}
					>
						<H2>Search</H2>
						<Button onPress={closeSearch} flexDirection="row" alignItems="center" gap={8}>
							<CloseIcon color="#1A428A" />
							<Typography sx={{ color: "$primary" }}>Close</Typography>
						</Button>
					</Box>
				)}

				<HStack gap={24} alignItems="center">
					<Box marginRight={"auto"}>
						<Link href="/">
							<Image resizeMode="contain" source={logo} alt="Abco: The Power to Clean aa" style={{ width: 172, height: 44 }} />
						</Link>
					</Box>
					{/* <Box borderLeftWidth={1} borderColor="$neutral200" height={44} width={1} /> */}
					<Box justifyContent={"center"}>
						<HStack sx={{ gap: 20 }} role="navigation">
							{topNavigation.map(({ title, href }: any, key) => {
								let isSelected = href === "/" ? currentPath === href : currentPath.startsWith(href);
								const isHovered = hoveredItem === href;
								return (
									<Box key={href} sx={{ position: "relative" }}>
										<Link href={href} asChild>
											<Pressable
												onHoverIn={() => handleNavHover(href)}
												onPress={() => setMegamenuOpen(false)}
												onHoverOut={handleNavLeave}
												// @ts-ignore - Web-specific style for removing focus outline
												style={{ outline: "none" }}
											>
												<Typography weight={isSelected || isHovered ? "bold" : "normal"}>{title}</Typography>
											</Pressable>
										</Link>
										{isSelected && <Box sx={{ position: "absolute", bottom: -12, left: 0, right: 0, height: 4, backgroundColor: "$primarylightblue" }} />}
									</Box>
								);
							})}
						</HStack>
					</Box>
					<HStack marginLeft={"auto"} space="lg" mx="$0.5" style={{ marginLeft: "auto" }}>
						{subNavigation.map((props: any, key) => {
							const { title, href, icon } = props;
							let isSelected = props.href == currentPath;

							// Check if we're in admin or finance section explicitly
							if (props.href == "/admin") {
								isSelected = isAdminSection;

								if (!isSuperAdmin) {
									return null;
								}
							}

							if (props.href == "/finance") {
								// Check if we're on a finance path
								isSelected = currentPath === "/finance" || currentPath.startsWith("/finance/");

								if (!isSuperAdmin) {
									return null;
								}
							}

							if (props.href == "/reports") {
								isSelected = currentPath === "/reports" || currentPath.startsWith("/reports/");
								if (!isSuperAdmin && !canViewReports) {
									return null;
								}
							}

							return (
								<Box key={href} sx={{ position: "relative" }}>
									<Link href={href} asChild>
										<Pressable
											onHoverIn={() => {
												setHoveredItem(href);
												setMegamenuOpen(false);
											}}
											onHoverOut={() => {
												setHoveredItem(null);
											}}
											// @ts-ignore - Web-specific style for removing focus outline
											style={{ outline: "none", flexDirection: "row", alignItems: "center", gap: 8 }}
										>
											{icon && (
												<Box>
													<Icon color="$primarylightblue" as={icon} size={"lg"} />
												</Box>
											)}
											<Typography weight={isSelected || hoveredItem === href ? "bold" : "normal"}>{title}</Typography>
										</Pressable>
									</Link>
									{isSelected && <Box sx={{ position: "absolute", bottom: -12, left: 0, right: 0, height: 4, backgroundColor: "$primarylightblue" }} />}
								</Box>
							);
						})}
					</HStack>
				</HStack>
				{megamenuOpen && (
					<div
						onMouseEnter={handleMegamenuHover}
						onMouseLeave={handleMegamenuLeave}
						style={{
							position: "absolute",
							left: 0,
							right: 0,
							top: "100%",
							zIndex: 5,
							backgroundColor: "#1A428A",
							border: "1px solid #e5e7eb",
							borderTop: "none",
							boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
							width: "100%",
							borderBottomLeftRadius: 8,
							borderBottomRightRadius: 8,
							overflow: "hidden",
							// Animation properties
							opacity: 1,
							transform: megamenuOpen ? "translateY(0) scaleY(1)" : "translateY(0) scaleY(0)",
							transformOrigin: "top",
							transition: "transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.15s ease-out",
							animation: "megamenu-appear 0.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
						}}
					>
						<div
							style={{
								padding: 32,
								paddingTop: 36,
								paddingBottom: 36,
								animation: "megamenu-content-appear 0.15s ease-out 0.1s both",
							}}
						>
							{hoveredNavItem && <MegaMenuContent hoveredNavItem={hoveredNavItem} onCloseMegamenu={handleMegamenuLeave} />}
						</div>
					</div>
				)}
			</Box>
			{/* <ApiActivity /> */}
		</Container>
	);
};

export default React.memo(HeaderPrimary);
